import { Helmet } from "react-helmet-async";
import { SITE } from "@/data/siteData";

/**
 * Centralised SEO Head: meta tags, Open Graph, Twitter, canonical, JSON-LD
 */
export default function SeoHead({
  title,
  description,
  canonicalPath = "/",
  image,
  type = "website",
  jsonLd = null,
  noIndex = false,
}) {
  const url = `${SITE.baseUrl}${canonicalPath}`;
  const ogImage = image || SITE.logo;
  const fullTitle = title || `${SITE.name} - Nettoyage Hotte Restaurant Seine-et-Marne`;
  const desc =
    description ||
    "Dégraissage hotte professionnelle à Melun et en Seine-et-Marne. Intervention 7j/7. Attestation officielle. Devis gratuit au 07 64 15 38 77.";

  return (
    <Helmet>
      <html lang="fr" />
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      {!noIndex && <meta name="robots" content="index, follow, max-image-preview:large" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="fr_FR" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {/* Geo */}
      <meta name="geo.region" content="FR-77" />
      <meta name="geo.placename" content="Melun" />
      <meta name="geo.position" content={`${SITE.address.lat};${SITE.address.lng}`} />
      <meta name="ICBM" content={`${SITE.address.lat}, ${SITE.address.lng}`} />

      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}

export const localBusinessJsonLd = (extra = {}) => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": `${SITE.baseUrl}#localbusiness`,
  name: SITE.name,
  image: SITE.logo,
  logo: SITE.logo,
  url: SITE.baseUrl,
  telephone: SITE.phoneTel,
  email: SITE.email,
  priceRange: "€€",
  address: {
    "@type": "PostalAddress",
    streetAddress: SITE.address.street,
    addressLocality: SITE.address.city,
    postalCode: SITE.address.postalCode,
    addressRegion: SITE.address.region,
    addressCountry: SITE.address.country,
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: SITE.address.lat,
    longitude: SITE.address.lng,
  },
  openingHoursSpecification: [{
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"],
    opens: "00:00",
    closes: "23:59",
  }],
  areaServed: [
    "Melun","Le Mée-sur-Seine","Dammarie-les-Lys","Vaux-le-Pénil","Savigny-le-Temple",
    "Cesson","Vert-Saint-Denis","Rubelles","La Rochette","Moissy-Cramayel",
    "Combs-la-Ville","Lieusaint","Fontainebleau","Avon","Bois-le-Roi",
    "Chartrettes","Le Châtelet-en-Brie","Nangis","Montereau-Fault-Yonne","Moret-Loing-et-Orvanne",
    "Seine-et-Marne",
  ],
  ...extra,
});

export const faqJsonLd = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export const breadcrumbJsonLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE.baseUrl}${it.path}`,
  })),
});
